<!--
  src/views/CourseTasks.vue
  Course-scoped tasks page. One EntityListLayout with:
    Tab "memberships" (stage 1) — the course's TasksBankCourse list (Search).
        A task is linked to the course or not; program-type independent. Read-only-ish (link/unlink).
    One tab PER program type (stage 2) — that plan's structured placements (StructuredPlan/Search).
        Each plan tab has three views: list / calendar / day. The tab itself IS the program-type scope.
  Course id is taken from the store (nav is already per-course).
-->
<template>
  <div class="Tasks__View" dir="rtl">
    <!-- EntityListLayout is ALWAYS rendered so its header (title + tabs + the toggle/
         action button in #button) stays in one fixed place across every view.
         In calendar/day the layout's TABLE body is hidden via .is-hiding-table, so
         only the header/tabs remain and the calendar/day render below it. This keeps
         the toggle + "+" button anchored — they never move when switching views. -->
    <EntityListLayout ref="list" :tabs="tabs" namespace="courseTasks"
      :class="{ 'is-hiding-table': isPlanTab && view !== 'list' }">
      <template #title><h1>משימות</h1></template>
      <template v-slot:counter="slotProps">
        סה"כ <b>{{ getTotalRecords(slotProps) }} </b>
        <!-- memberships tab: running duration of the checked tasks, to fill a plan day before placing -->
        <span v-if="selectedMemberships(slotProps.tab).length">
          | נבחרו <b>{{ selectedMemberships(slotProps.tab).length }}</b> משימות, סה״כ זמן
          <b>{{ toHours(totalMinutes(selectedMemberships(slotProps.tab))) }}</b>
        </span>
      </template>
      <template #button>
        <div class="Tasks__ListActions">
          <!-- View toggle sits beside the action button. On the memberships tab it's
               shown disabled (only plan tabs have calendar/day views) so its position
               stays consistent across tabs. -->
          <div class="Tasks__ViewToggle">
            <div class="toggle" :class="{ 'is-disabled': !isPlanTab }">
              <button :class="{ active: activeView === 'list' }" :disabled="!isPlanTab" @click="view = 'list'">
                <b-icon icon="format-list-bulleted" size="is-small" /> רשימה
              </button>
              <button :class="{ active: activeView === 'calendar' }" :disabled="!isPlanTab" @click="view = 'calendar'">
                <b-icon icon="calendar-month" size="is-small" /> לוח חודשי
              </button>
              <button :class="{ active: activeView === 'day' }" :disabled="!isPlanTab" @click="view = 'day'">
                <b-icon icon="calendar-today" size="is-small" /> יומי
              </button>
            </div>
          </div>
          <b-button type="is-primary" icon-left="plus" @click="openAssign()">{{ assignButtonLabel }}</b-button>
        </div>
      </template>
    </EntityListLayout>

    <!-- ===== CALENDAR (plan tab only) ===== -->
    <div v-if="isPlanTab && view === 'calendar'" class="Tasks__Calendar">
      <Calendar :show-hint="false" @month-change="onMonthChange">
        <template #cell-body="{ cell }">
          <div class="task-chips">
            <div v-for="t in tasksByDate[cell.dateStr] || []" :key="t.id" class="task-chip" @click.stop="openAssign(t)">
              <span class="dot" :style="{ background: timeColor(t.timeWindow) }"></span>
              <span class="txt">{{ t.taskName }}</span>
            </div>
            <!-- gentle affordances on the day-number row: open the daily view, or add a task -->
            <button type="button" class="open-day" title="פתח יום" @click.stop="openDay(cell.dateStr)">
              <b-icon icon="calendar-today" size="is-small" />
            </button>
            <!-- per-day note: filled when the day has one -->
            <button type="button" class="day-note" :class="{ 'has-note': !!dayComments[cell.dateStr] }"
              :title="dayComments[cell.dateStr] || 'הוסף הערה ליום'"
              @click.stop="openDayComments(cell.dateStr)">
              <b-icon :icon="dayComments[cell.dateStr] ? 'comment-text' : 'comment-plus-outline'" size="is-small" />
            </button>
            <span v-if="!cell.isPast"
                  class="add" @click.stop="openAssign(null, cell.dateStr)">+</span>
          </div>
        </template>
      </Calendar>
    </div>

    <!-- ===== DAY (plan tab only) ===== -->
    <div v-if="isPlanTab && view === 'day'" class="Tasks__Day">
      <PlanDayOrder :program-type-id="activeProgramTypeId" :initial-date="selectedDayDate"
        @day-comments-saved="onDayCommentsSaved" @task-unassigned="onTaskUnassigned" />
    </div>
  </div>
</template>

<script>
import EntityListLayout from "@/components/EntityListLayout.vue";
import Calendar from "@/components/Calendar.vue";
import PlanDayOrder from "@/components/PlanDayOrder.vue";
import DialogAssignTask from "@/components/DialogAssignTask.vue";
import DialogAddTask from "@/components/DialogAddTask.vue";
import DialogLinkBankTask from "@/components/DialogLinkBankTask.vue";
import DialogPlaceInPlan from "@/components/DialogPlaceInPlan.vue";
import DialogMovePlanTasks from "@/components/DialogMovePlanTasks.vue";
import DialogDayComments from "@/components/DialogDayComments.vue";
import FieldService from "@/services/FieldService";
import FileService from "@/services/FileService";
import FilterService from "@/services/FilterService";
import LessonService from "@/services/LessonService";
import TaskDuration from "@/services/TaskDuration";
import { Overlay } from "buefy"; // eslint-disable-line no-unused-vars
import TasksService from "../services/TasksService";

const MEMBERSHIPS_TAB = "CourseMemberships";
// One plan tab per program type; its id encodes the program-type value: "plan-<value>".
const PLAN_TAB_PREFIX = "plan-";
const planTabId = (value) => `${PLAN_TAB_PREFIX}${value}`;
const programTypeOfTabId = (id) =>
  typeof id === "string" && id.startsWith(PLAN_TAB_PREFIX)
    ? Number(id.slice(PLAN_TAB_PREFIX.length))
    : null;

const TIME_WINDOW = [
  { Value: "any", Text: "כל היום", color: "#00427e" },
  { Value: "morning", Text: "בוקר", color: "#00afd8" },
  { Value: "evening", Text: "ערב", color: "#E1583D" },
];

export default {
  name: "CourseTasks",
  components: { EntityListLayout, Calendar, PlanDayOrder },
  data() {
    return {
      view: "list",
      activeTabId: MEMBERSHIPS_TAB, // memberships is the primary tab
      programTypes: [],
      selectedDayDate: null,    // "YYYY-MM-DD" the day view opens on (set from a calendar day click)
      tasksByDate: {},          // { "2026-09-14": [ {id,title,timeWindow}, ... ] }
      // Known per-day notes: { "2026-09-14": "..." }. The API is per-day (no bulk read), so this
      // fills in lazily — a day is added once its note is opened or saved — rather than firing
      // one request per visible cell on every month change.
      dayComments: {},
      calMonth: new Date().getMonth(),
      calYear: new Date().getFullYear(),
    };
  },
  computed: {
    courseId() {
      return this.$store.state.auth.course.id;
    },
    // A plan tab (stage 2) owns calendar/day views + a program type; memberships (stage 1) doesn't.
    isPlanTab() {
      return programTypeOfTabId(this.activeTabId) != null;
    },
    // Program type of the currently active plan tab (null on the memberships tab).
    activeProgramTypeId() {
      return programTypeOfTabId(this.activeTabId);
    },
    // Which toggle button reads as active. The memberships tab only has a list,
    // so force "list" there even if `view` still holds a plan tab's calendar/day.
    activeView() {
      return this.isPlanTab ? this.view : "list";
    },
    // Memberships tab links a task FROM THE BANK; plan tabs place a task already linked to the course.
    assignButtonLabel() {
      return this.isPlanTab ? "שיבוץ משימה בתכנית" : "שיוך משימה מהבנק";
    },
    tabs() {
      return [this.membershipsTab, ...this.planTabs];
    },
    membershipsTab() {
      const membershipFields = FieldService.list("courseMemberships");
      return {
        // ===== Stage 1 — course task memberships (program-type independent) =====
        id: MEMBERSHIPS_TAB,
        title: "משימות משויכות לקורס",
        isPrimary: true,
        fastSearch: true,
        fastSearchOptions: { propName: "Terms", label: "חיפוש..." },
        checkableRows: true,
        perPage: 500,
        page: 1,
        sortField: "name",
        sortOrder: "asc",
        selectedRows: [],
        getDataAsync: this.getMembershipTasks,
        sideViewModal: DialogAddTask,   // row click -> view/edit the bank task itself
        tableActions: [
          { Id: "placeInPlan", Label: "שבץ בתכנית", icon: "calendar-plus", click: this.openPlaceInPlan },
          { Id: "unlink", Label: "הסר מהקורס", icon: "delete", click: this.bulkUnlink },
        ],
        fields: [
          { ...membershipFields.TasksBankId, checked: true },
          { ...membershipFields.Name, checked: true },
          { ...membershipFields.SubjectName, checked: true },
          { ...membershipFields.Description, checked: true },
          { ...membershipFields.DurationMinutes, checked: true },
          { ...membershipFields.Tags, checked: true },
          { ...membershipFields.IsActive, checked: true },
        ],
        filters: [
          { Display: "סטטוס", Query: "Status", Type: "FilterSelect", AllowEmpty: true,
            Options: [{ Value: 1, Text: "פעיל" }, { Value: 0, Text: "לא פעיל" }] },
        ],
      };
    },
    // ===== Stage 2 — one structured-plan tab per program type =====
    planTabs() {
      const taskFields = FieldService.list("courseTasks");
      const taskFilters = FilterService.list();
      return this.programTypes.map((p) => ({
        id: planTabId(p.Value),
        title: `תכנית ${p.Text}`,
        fastSearch: true,
        fastSearchOptions: { propName: "Terms", label: "חיפוש מהיר..." },
        checkableRows: true,
        perPage: 500,
        page: 1,
        sortField: "taskDate",
        sortOrder: "asc",
        selectedRows: [],
        // Bind each plan tab to its own program type (getDataAsync doesn't know which tab called it).
        getDataAsync: (query) => this.getPlanTasks(p.Value, query),
        sideViewModal: DialogAssignTask,   // click a row to edit the assignment (opens the same modal)
        tableActions: [
          { Id: "moveDate", Label: "העבר לתאריך אחר", icon: "calendar-arrow-right", click: this.openMovePlanTasks },
          { Id: "unassign", Label: "בטל שיוך", icon: "delete", click: this.bulkUnassign },
        ],
        fields: [
          { ...taskFields.TasksBankId, checked: true },
          { ...taskFields.SubjectName, checked: true },
          { ...taskFields.TaskName, checked: true },
          { ...taskFields.Description, checked: true },
          { ...taskFields.DurationMinutes, checked: true },
          { ...taskFields.TaskDate, checked: true },
          { ...taskFields.TimeWindow, checked: true },
          { ...taskFields.SortOrder, checked: true },
          { ...taskFields.Status, checked: true },
        ],
        filters: [
          // ProgramType scope is the tab itself, so no filter here.
          { ...taskFilters.TaskDateRange },
          { ...taskFilters.TaskTimeWindow },
          { ...taskFilters.TaskStatus },
        ],
        excelExport: [
          { title: "ייצוא לאקסל", action: this.exportBank },
        ],
      }));
    },
  },
  created() {
    // Restore the active tab from the URL so isPlanTab / activeProgramTypeId are correct on reload/deep-link.
    const { tab } = this.$route.query;
    if (tab) this.activeTabId = tab;
    TasksService.getProgramTypes().then((r) => {
      this.programTypes = (r.data || []).map((i) => ({ Value: i.id, Text: i.name }));
    });
  },
  methods: {
    getTotalRecords(slotProps) {
      return slotProps.tab && slotProps.tab.total && slotProps.tab.total;
    },
    // Checked rows of the memberships tab only — plan tabs show no selection total.
    selectedMemberships(tab) {
      return tab && tab.id === MEMBERSHIPS_TAB ? tab.selectedRows : [];
    },
    totalMinutes: TaskDuration.totalMinutes,
    toHours: TaskDuration.toHours,
    // Stage 1 — paged membership search. Terms/Status/paging/sort flow through from the layout;
    // each membership's nested bank task is flattened onto the row so the flat field Ids
    // (name, subjectName, ...) resolve, while keeping the membership id.
    getMembershipTasks({ cancelToken, ...rest }) {
      return TasksService.getCourseTaskMemberships(
        this.courseId,
        { ...rest },
        cancelToken && cancelToken.token
      ).then((r) => {
        const items = (r.data && r.data.items) || [];
        const rows = items.map((m) => ({
          ...(m.task || {}),
          tasksBankCourseId: m.tasksBankCourseId,
          courseId: m.courseId,
        }));
        const total = r.data && r.data.totalResults != null ? r.data.totalResults : rows.length;
        return { data: { TotalResults: total, Items: rows } };
      });
    },
    // Stage 2 — placements for one program type (the calling plan tab).
    getPlanTasks(programTypeId, { cancelToken, ...rest }) {
      return TasksService.getCourseTasks(
        {
          ...rest,
          CourseId: this.courseId,
          ProgramTypeId: programTypeId,
        },
        cancelToken && cancelToken.token
      ).then((r) => ({
        data: {
          TotalResults: r.data.totalResults,
          Items: r.data.items,
        },
      }));
    },
    timeColor(w) {
      const t = TIME_WINDOW.find((x) => x.Value === w);
      return t ? t.color : "#00427e";
    },
    timeLabel(w) {
      const t = TIME_WINDOW.find((x) => x.Value === w);
      return t ? t.Text : "";
    },
    onMonthChange({ month, year }) {
      this.calMonth = month;
      this.calYear = year;
      this.loadCalendar();
    },
    loadCalendar() {
      if (this.activeProgramTypeId == null) return;
      // Ask for all rows (no paging) so every day is populated.
      TasksService.getCourseTasks({
        CourseId: this.courseId,
        ProgramTypeId: this.activeProgramTypeId,
        PageSize: 9999,
        PageNumber: 1,
      }).then((r) => {
        const items = r.data.items || [];
        const map = {};
        items.forEach((t) => {
          if (!t.taskDate) return;
          const d = t.taskDate.slice(0, 10); // "2026-07-15T00:00:00" -> "2026-07-15"
          (map[d] = map[d] || []).push(t);
        });
        this.tasksByDate = map;
      });
    },
    // Keep the calendar's indicators in step with a note saved in the day view.
    onDayCommentsSaved({ date, comments }) {
      this.$set(this.dayComments, date, comments);
    },
    // A placement removed in the day view: keep the calendar and the list tab in step.
    onTaskUnassigned() {
      this.loadCalendar();
      if (this.$refs.list) this.$refs.list.onNewEntitySave();
    },
    // Edit the per-day admin note straight from a calendar cell.
    openDayComments(dateStr) {
      if (!dateStr || this.activeProgramTypeId == null) return;
      this.$buefy.modal.open({
        parent: this,
        component: DialogDayComments,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          courseId: this.courseId,
          programTypeId: this.activeProgramTypeId,
          date: dateStr,
          onLoaded: (date, comments) => { this.$set(this.dayComments, date, comments); },
          onSuccess: (date, comments) => { this.$set(this.dayComments, date, comments); },
        },
      });
    },
    // Open the daily view for a specific date (from a monthly-calendar day click).
    openDay(dateStr) {
      if (!dateStr) return;
      this.selectedDayDate = dateStr;
      this.view = "day";
    },
    // "שיוך משימה מהבנק" — different per tab:
    //   memberships (stage 1) -> link bank task to course (no program type / date)
    //   plan (stage 2)        -> place a task on a date (DialogAssignTask)
    openAssign(task = null, date = null) {
      if (!this.isPlanTab) {
        this.openLinkBankTask();
        return;
      }
      this.$buefy.modal.open({
        parent: this,
        component: DialogAssignTask,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          store: this.$store,
          row: task,
          defaultDate: date,
          defaultProgramTypeId: this.activeProgramTypeId, // pre-select the active plan tab's type
          onSuccess: () => {
            // Refresh both views so the change is reflected regardless of which is open next.
            this.loadCalendar();
            if (this.$refs.list) this.$refs.list.onNewEntitySave();
          },
        },
      });
    },
    openLinkBankTask() {
      this.$buefy.modal.open({
        parent: this,
        component: DialogLinkBankTask,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          store: this.$store,
          onSuccess: () => { if (this.$refs.list) this.$refs.list.onNewEntitySave(); },
        },
      });
    },
    // Stage 2 (bulk) — place the selected linked tasks into a plan (rows carry tasksBankCourseId).
    openPlaceInPlan(rows, refresh) {
      if (!rows || !rows.length) return;
      this.$buefy.modal.open({
        parent: this,
        component: DialogPlaceInPlan,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          courseId: this.courseId,
          tasks: rows,
          onSuccess: refresh,
        },
      });
    },
    // Stage 1 — unlink the selected bank tasks from this course. Cascades to plan placements,
    // so confirm first. Rows are flattened memberships: `id` is the bank task id.
    bulkUnlink(rows, refresh) {
      if (!rows || !rows.length) return;
      this.$buefy.dialog.confirm({
        title: "הסרת משימות מהקורס",
        message: `הסרת ${rows.length} משימות מהקורס תסיר אותן גם מכל שיבוץ בתוכניות הקורס. להמשיך?`,
        confirmText: "הסר",
        cancelText: "ביטול",
        type: "is-danger",
        onConfirm: () => {
          Promise.all(
            rows.map((r) => TasksService.unlinkTaskFromCourse(r.id, this.courseId))
          )
            .then(() => {
              this.$buefy.toast.open({ type: "is-success", message: "המשימות הוסרו מהקורס.", duration: 4000 });
              refresh();
            })
            .catch(() => {
              this.$buefy.toast.open({ type: "is-danger", message: "הסרת המשימות נכשלה.", duration: 6000 });
            });
        },
      });
    },
    // Stage 2 (bulk) — move the selected placements to another date. Rows may span days;
    // they all land on the one date picked in the dialog. Refresh the calendar too — the
    // move changes which day the tasks sit on.
    openMovePlanTasks(rows, refresh) {
      if (!rows || !rows.length) return;
      this.$buefy.modal.open({
        parent: this,
        component: DialogMovePlanTasks,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          tasks: rows,
          onSuccess: () => {
            this.loadCalendar();
            refresh();
          },
        },
      });
    },
    // Stage 2 (bulk) — drop the selected placements from the plan. One call for any
    // number of rows; the server's message already handles singular/plural.
    bulkUnassign(rows, refresh) {
      if (!rows || !rows.length) return;
      const one = rows.length === 1;
      this.$buefy.dialog.confirm({
        title: "ביטול שיוך",
        message: one
          ? `לבטל את שיוך המשימה "${rows[0].taskName || rows[0].name}" מהתכנית?`
          : `לבטל את שיוך ${rows.length} המשימות מהתכנית?`,
        confirmText: "בטל שיוך",
        cancelText: "ביטול",
        type: "is-danger",
        onConfirm: () => {
          TasksService.deleteStructuredPlanTask(rows.map((r) => r.id))
            .then((r) => {
              const d = r.data || {};
              this.$buefy.toast.open({
                type: "is-success",
                message: d.message || "השיוך בוטל.",
                duration: 4000,
              });
              // Some ids were already gone (concurrent edit / double click) — the refresh
              // below realigns the view either way, but don't let it pass silently.
              if (d.deletedCount != null && d.requestedCount != null && d.deletedCount < d.requestedCount) {
                this.$buefy.toast.open({
                  type: "is-warning",
                  message: "חלק מהמשימות כבר לא היו בתוכנית. הרשימה עודכנה.",
                  duration: 6000,
                });
              }
              this.loadCalendar();
              refresh();
            })
            .catch(() => {
              this.$buefy.toast.open({ type: "is-danger", message: "ביטול השיוך נכשל.", duration: 6000 });
            });
        },
      });
    },
    exportBank({ loading }) {
      loading(true);
      TasksService.exportTasksBank(this.courseId, this.activeProgramTypeId)
        .then((r) => FileService.saveXlsx(r.data, r.headers, "tasks-bank.xlsx"))
        .catch(() => {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "ייצוא בנק המשימות נכשל.",
            duration: 6000,
          });
        })
        .finally(() => loading(false));
    },
  },
  watch: {
    view(v) { if (v === "calendar") this.loadCalendar(); },
    // EntityListLayout keeps the active tab in the ?tab= query; mirror it so isPlanTab /
    // activeProgramTypeId track the selected tab.
    "$route.query.tab": function onTabQuery(tab) {
      if (tab) this.activeTabId = tab;
    },
    // Switching plan tabs: the list reloads itself via getDataAsync; refresh the calendar if it's open.
    activeProgramTypeId() {
      if (this.isPlanTab && this.view === "calendar") this.loadCalendar();
    },
  },
};
</script>

<style scoped lang="scss">
.Tasks__View {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.Tasks__ListActions {
  display: flex;
  align-items: center;
  gap: 12px;
}

// Calendar/day views: keep the layout's header + tab strip (so the toggle and
// "+" button stay anchored), but collapse its table body so the calendar/day
// below it isn't pushed off-screen.
// `.is-hiding-table` is applied to EntityListLayout's ROOT (which carries this
// component's scope id), so it matches WITHOUT ::v-deep; its inner elements
// belong to the child scope and need ::v-deep.
.is-hiding-table {
  flex: 0 0 auto;
  height: auto !important;   // override the layout's own `height: 100%`
  ::v-deep .Entity__View__Tabs__Content { display: none; }
  ::v-deep .Entity__View__Tabs { flex: 0 0 auto; }
}

.Tasks__ViewToggle {
  display: flex;
  justify-content: flex-start;
  .toggle {
    display: flex;
    background: #eaf0f6;
    border-radius: 6px;
    padding: 3px;
    button {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: none;
      background: transparent;
      color: #6b7b8c;
      font-weight: 600;
      font-size: 13.5px;
      padding: 7px 16px;
      border-radius: 5px;
      cursor: pointer;
      &.active { background: #fff; color: #00427e; box-shadow: 0 1px 3px rgba(12, 41, 73, 0.18); }
      &:disabled { cursor: default; }
    }
    // Memberships tab: whole toggle is inert and dimmed.
    &.is-disabled {
      opacity: 0.5;
      button { pointer-events: none; }
    }
  }
}

.Tasks__Calendar {
  padding: 0 40px 16px;
  // Fit the whole calendar within the viewport so it never scrolls.
  // 50px top bar (layout) + shared toolbar + this view's padding ≈ 210px of chrome.
  height: calc(100vh - 210px);
  // Full page width (same inset as the list view) so switching views never
  // changes the content-area width.
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;

  // Make the Calendar fill the remaining height and let day rows share it.
  ::v-deep .calendar {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  ::v-deep .days-grid {
    flex: 1 1 auto;
    min-height: 0;
    grid-auto-rows: 1fr;   // rows split the available height evenly
  }
  ::v-deep .day-cell {
    min-height: 0;          // drop the fixed 90px so rows can size to fit
    justify-content: flex-start;
    overflow: hidden;       // long chip lists scroll within the cell, not the page
    position: relative;     // anchor the absolutely-placed "+" to the cell
  }
  // Let the slot body fill the cell and become the bounded scroll context.
  ::v-deep .cell-body-center {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    justify-content: flex-start;
  }
}

// .Tasks__Day intentionally has no styles — PlanDayOrder owns its own full-bleed
// gray background + padding.

.task-chips {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-y: auto;   // busy days scroll their chips inside the cell
  .task-chip { flex: 0 0 auto; }
  .task-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #f0f4f8;
    border-radius: 4px;
    padding: 3px 6px;
    cursor: pointer;
    &:hover { background: #e6edf4; }
    .dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
    .txt { font-size: 11px; color: #0c2949; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }
  // Gentle "open daily view" icon on the day-number row, just inside the "+" (top-left in RTL).
  .open-day {
    position: absolute;
    top: 6px;
    left: 28px;
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    border: none; background: transparent;
    color: #9fb2c6; cursor: pointer; padding: 0;
    z-index: 1;
    transition: color 0.12s;
    &:hover { color: #00427e; }
  }
  // Per-day note icon, alongside the other day-number-row affordances.
  .day-note {
    position: absolute;
    top: 6px;
    left: 50px;
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    border: none; background: transparent;
    color: #9fb2c6; cursor: pointer; padding: 0;
    z-index: 1;
    transition: color 0.12s;
    &:hover { color: #00427e; }
    // A day that already has a note reads as filled, not as an affordance.
    &.has-note { color: #E1583D; }
  }
  // "+" sits on the same row as the day number (top-left corner in RTL).
  .add {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 18px; height: 18px; line-height: 16px; text-align: center;
    border: 1px solid #cbd6e2; border-radius: 50%;
    color: #00427e; cursor: pointer; font-size: 13px;
    background: #fff;
    z-index: 1;
  }
}
</style>
